import axios from "axios";
import { API_URL } from "../constants";
import { upload } from "./upload-file.service";

/** The employment terms an amendment changes; a term left null is not touched. */
export interface EmploymentTermsValues {
  newPositionIds: string[] | null;
  newSalary: number | null;
  newHoursPerWeek: number | null;
}

export interface AmendmentChange {
  point: string;
  text: string;
  action: string;
}

/** What an old paper-signed amendment says: the part a registration and a later edit both send. */
export interface RegisteredAmendmentData extends Partial<EmploymentTermsValues> {
  amendmentNumber: string;
  /** Epoch ms. */
  effectiveDate: number;
  changes?: AmendmentChange[];
  isEmploymentTermsChange?: boolean;
}

/** Its document is either a fresh upload or a document already in the tree — exactly one. */
export type RegisterAmendmentData = RegisteredAmendmentData &
  ({ fileId: string; existingFileId?: undefined } | { existingFileId: string; fileId?: undefined });

export async function fetchAmendments(companyId: string, onboardingId: string) {
  const res = await axios.get(`${API_URL}/${companyId}/onboarding/${onboardingId}/amendments`);
  return res.data.amendments;
}

export async function createAmendment(companyId: string, onboardingId: string, data: any) {
  const res = await axios.post(`${API_URL}/${companyId}/onboarding/${onboardingId}/amendments`, data);
  return res.data;
}

export async function registerAmendment(companyId: string, onboardingId: string, data: RegisterAmendmentData) {
  const res = await axios.post(`${API_URL}/${companyId}/onboarding/${onboardingId}/amendments/register`, data);
  return res.data;
}

/** Replaces the number, date, changes and terms of an amendment that was registered from a scan. */
export async function updateRegisteredAmendment(companyId: string, id: string, data: RegisteredAmendmentData) {
  const res = await axios.patch(`${API_URL}/${companyId}/contract-amendments/${id}/registered`, data);
  return res.data;
}

export async function updateAmendment(companyId: string, id: string, data: any) {
  const res = await axios.patch(`${API_URL}/${companyId}/contract-amendments/${id}`, data);
  return res.data;
}

export async function deleteAmendment(companyId: string, id: string) {
  await axios.delete(`${API_URL}/${companyId}/contract-amendments/${id}`);
}

export async function generateAmendment(companyId: string, id: string) {
  const res = await axios.post(`${API_URL}/${companyId}/contract-amendments/${id}/generate`);
  return res.data;
}

export async function sendAmendmentForSignature(companyId: string, id: string, data: { signMethod: string; selectedTabUser?: string }) {
  const res = await axios.post(`${API_URL}/${companyId}/contract-amendments/${id}/send-for-signature`, data);
  return res.data;
}

export async function uploadSignedAmendment(companyId: string, id: string, data: { fileId: string }) {
  const res = await axios.post(`${API_URL}/${companyId}/contract-amendments/${id}/upload-signed`, data);
  return res.data;
}

/** Adds the structured terms to a signed amendment that was written as free text only. */
export async function setAmendmentTerms(companyId: string, id: string, data: Partial<EmploymentTermsValues>) {
  const res = await axios.post(`${API_URL}/${companyId}/contract-amendments/${id}/set-terms`, data);
  return res.data;
}

/** Uploads a scanned file for an amendment and returns its id. */
export async function uploadAmendmentFile(file: File, companyId: string): Promise<string> {
  const uploadRes = await upload(file, companyId);
  // The upload endpoint has answered with the id in more than one shape.
  const fileId = uploadRes?.data?.id || uploadRes?.data?.file?.id || uploadRes?.data;
  if (!fileId) {
    throw new Error("Uploaded file id not found");
  }
  return fileId;
}

export async function revokeAmendment(companyId: string, id: string) {
  const res = await axios.post(`${API_URL}/${companyId}/contract-amendments/${id}/revoke`);
  return res.data;
}

export async function completeAmendmentWsRevision(
  companyId: string,
  id: string,
  data: { confirmNoRoles?: boolean; acknowledgeDoclessRoles?: boolean } = {}
) {
  const res = await axios.post(
    `${API_URL}/${companyId}/contract-amendments/${id}/complete-ws-revision`,
    data
  );
  return res.data;
}
